import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { CanonicalQuote } from "../../../types/lifecycle.ts";
import { PARTY_KEYS, type PartyKey } from "./model.ts";
import {
  LifecycleRecords,
  changedRecord,
  partyFromRecord,
  partyPayload,
  personKey,
  vehicleKey,
  validPersonKey,
  vehicleFromRecord,
  vehicleInputsFromRecord,
  vehiclePayload,
  type VehicleCatalogs,
  type RecordEntry,
} from "./localRecords.ts";
import type { useLifecycleQuote } from "./useLifecycleQuote.ts";
import { useLookup } from "./useLookup.ts";

type QuoteForm = ReturnType<typeof useLifecycleQuote>;
const EMPTY_CATALOGS: VehicleCatalogs = { types: {}, usages: {} };
const EMPTY_ENTRY: RecordEntry = {
  record: null,
  loaded: false,
  loading: false,
  saving: false,
  error: null,
};

export function useLifecycleRecords(
  jwt: string,
  account: string,
  form: QuoteForm
) {
  const store = useMemo(() => new LifecycleRecords(jwt), [jwt, account]);
  const entries = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const catalogs = useLookup(
    form.draft ? JSON.stringify([jwt, account, catalogRevision]) : "",
    () => store.getCatalogs(catalogRevision > 0)
  );
  const latest = useRef({ form, store });
  latest.current = { form, store };
  const parties = Object.fromEntries(
    PARTY_KEYS.map((party) => {
      const view = form.partyEdits[party]?.values ?? form.draft?.[party];
      const identity = personKey(view?.pin ?? "");
      const valid = validPersonKey(identity);
      const entry = entries[`client:${identity}`] ?? EMPTY_ENTRY;
      const payload = view ? partyPayload(view, entry.record) : null;
      const changed = Boolean(
        payload && entry.loaded && changedRecord(payload, entry.record)
      );
      const validation = !valid
        ? "Въведете ЕГН / ЕИК за търсене."
        : !payload?.NAME_INS
        ? "Попълнете името преди запис."
        : null;
      return [
        party,
        {
          ...entry,
          identity,
          valid,
          payload,
          changed,
          validation,
          loading: valid && (!entries[`client:${identity}`] || entry.loading),
          canSave: entry.loaded && !entry.saving && !validation && changed,
          id: entry.record ? Number(entry.record.CL_ID) : undefined,
        },
      ];
    })
  ) as Record<
    PartyKey,
    RecordEntry & {
      identity: string;
      valid: boolean;
      payload: ReturnType<typeof partyPayload> | null;
      changed: boolean;
      validation: string | null;
      canSave: boolean;
      id?: number;
    }
  >;
  const plate = form.draft ? vehicleKey(form.inputs.plate) : "";
  const entry = entries[`vehicle:${plate}`] ?? EMPTY_ENTRY;
  const vehicleView = form.vehicleEdits?.values ?? form.draft?.vehicle;
  const payload = vehicleView
    ? vehiclePayload(
        vehicleView,
        form.inputs,
        entry.record,
        catalogs.value ?? EMPTY_CATALOGS
      )
    : null;
  const vehicleChanged = Boolean(
    payload &&
      entry.loaded &&
      catalogs.value &&
      changedRecord(payload, entry.record)
  );
  const validation =
    payload && catalogs.value && (!payload.CAR_TYPE || !payload.CAR_USAGE)
      ? "Проверете вида и предназначението на автомобила."
      : null;
  const vehicle = {
    ...entry,
    payload,
    changed: vehicleChanged,
    validation,
    loading: Boolean(
      plate &&
        (!entries[`vehicle:${plate}`] || entry.loading || catalogs.loading)
    ),
    error:
      entry.error ||
      (catalogs.error
        ? "Неуспешно зареждане на видовете и предназначенията."
        : null),
    canSave:
      entry.loaded &&
      Boolean(catalogs.value) &&
      !entry.saving &&
      !validation &&
      vehicleChanged,
    id: entry.record ? Number(entry.record.CAR_ID) : undefined,
  };

  useEffect(() => {
    for (const party of PARTY_KEYS) {
      const current = parties[party];
      if (current.valid) void store.load("client", current.identity);
      if (current.record && current.loaded && !form.searching)
        form.hydrateParty(
          party,
          current.identity,
          partyFromRecord(current.record, current.saved)
        );
    }
    if (plate) void store.load("vehicle", plate);
    if (entry.record && entry.loaded && plate && !form.searching) {
      form.hydrateVehicle(
        plate,
        vehicleFromRecord(entry.record),
        vehicleInputsFromRecord(entry.record, catalogs.value ?? EMPTY_CATALOGS)
      );
    }
  });

  const saveParty = async (party: PartyKey) => {
    const current = parties[party];
    const buffer = form.partyEdits[party];
    if (!current.loaded || current.saving || current.validation) return;
    if (
      current.changed &&
      !(await store.save("client", current.identity, current.payload))
    )
      return;
    // Navigating to another vehicle/account while a save is in flight must not
    // commit an old person's buffer into the new draft.
    if (
      latest.current.store === store &&
      buffer &&
      latest.current.form.partyBuffers[party]?.edited === buffer.edited
    ) {
      const currentBuffer = latest.current.form.partyBuffers[party];
      latest.current.form.commitParty(
        party,
        currentBuffer.values,
        currentBuffer.copied
      );
    }
  };
  const saveVehicle = async () => {
    const buffer = form.vehicleEdits;
    if (
      !entry.loaded ||
      vehicle.loading ||
      vehicle.saving ||
      vehicle.validation ||
      (vehicle.error && !catalogs.value)
    )
      return;
    if (vehicle.changed && !(await store.save("vehicle", plate, payload)))
      return;
    if (
      latest.current.store === store &&
      buffer &&
      latest.current.form.vehicleBuffer?.edited === buffer.edited
    )
      latest.current.form.commitVehicle(
        latest.current.form.vehicleBuffer.values
      );
  };
  const saving = Object.values(entries).some((value) => value.saving);
  const prepareQuote = async (
    quote: CanonicalQuote
  ): Promise<CanonicalQuote> => {
    const registration = vehicleKey(quote.registrationPlate);
    const identities = Object.fromEntries(
      PARTY_KEYS.map((party) => [
        party,
        personKey(
          quote[party]?.pin ??
            form.draft?.[party].pin ??
            (party === "holder" ? quote.ownerIdentifier ?? "" : "")
        ),
      ])
    ) as Record<PartyKey, string>;
    // Lookups run together before contacting insurers. Failed Wing reads stay
    // visible on the cards but do not prevent obtaining an insurer quote.
    const [knownCatalogs] = await Promise.all([
      store.getCatalogs().catch(() => EMPTY_CATALOGS),
      store.load("vehicle", registration),
      ...PARTY_KEYS.filter((party) => validPersonKey(identities[party])).map(
        (party) => store.load("client", identities[party])
      ),
    ]);
    const snapshot = store.getSnapshot();
    const car = snapshot[`vehicle:${registration}`];
    const knownCar = car?.loaded ? car.record : null;
    const mappedInputs = knownCar
      ? vehicleInputsFromRecord(knownCar, knownCatalogs)
      : {};
    const vehicle = {
      ...quote.vehicle,
      ...(knownCar ? vehicleFromRecord(knownCar) : {}),
      ...(mappedInputs.vehicleType
        ? { vehicleType: mappedInputs.vehicleType }
        : {}),
      ...Object.fromEntries(
        Object.entries(quote.vehicle ?? {}).filter(
          ([key]) =>
            form.manual[`vehicle.${key}`] ||
            (key === "vehicleType" && form.manualInputs.vehicleType)
        )
      ),
    };
    const prepared: CanonicalQuote = {
      ...quote,
      ...(quote.purpose || mappedInputs.purpose
        ? {
            purpose: form.manualInputs.purpose
              ? quote.purpose
              : mappedInputs.purpose || quote.purpose,
          }
        : {}),
      ...(Object.keys(vehicle).length ? { vehicle } : {}),
    };
    for (const party of PARTY_KEYS) {
      const person = snapshot[`client:${identities[party]}`];
      if (!person?.loaded || !person.record) continue;
      const values = partyFromRecord(person.record, person.saved);
      // Phone is Wing contact data, not an insurer quote override.
      delete values.phone;
      prepared[party] = {
        ...quote[party],
        ...values,
        ...Object.fromEntries(
          Object.entries(quote[party] ?? {}).filter(
            ([key]) => form.manual[`${party}.${key}`]
          )
        ),
      };
    }
    return prepared;
  };
  return {
    parties,
    vehicle,
    saving,
    saveParty,
    saveVehicle,
    prepareQuote,
    retryParty: (party: PartyKey) =>
      store.load("client", parties[party].identity, true),
    retryVehicle: () => {
      if (plate) void store.load("vehicle", plate, true);
      if (catalogs.error) setCatalogRevision((value) => value + 1);
    },
    // Only the holder and vehicle are required by Wing's policy writer. Optional
    // owner/driver cards must not block issuance solely because they are unsaved.
    ready: Boolean(
      parties.holder.id &&
        parties.holder.loaded &&
        !parties.holder.changed &&
        vehicle.id &&
        entry.loaded &&
        Boolean(catalogs.value) &&
        !vehicle.loading &&
        !vehicle.changed &&
        !validation &&
        !saving
    ),
  };
}
